import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { invoiceAPI, revenueAPI } from '../../lib/api';
import { formatCurrency, formatCurrencyFull, formatMonth, isValidMonth } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
    FileText, 
    Upload,
    Loader2,
    DollarSign,
    Calendar
} from 'lucide-react';

const CreateInvoicePage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const presetMonth = searchParams.get('month') || '';
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [summary, setSummary] = useState(null);
    const [availablePeriods, setAvailablePeriods] = useState([]);
    const [formData, setFormData] = useState({
        month: presetMonth,
        notes: ''
    });
    const [attachment, setAttachment] = useState(null);

    useEffect(() => {
        const fetchSummary = async () => {
            try {
                // Pull revenue summary (for the "what's earned this month" card at top)
                // AND the un-invoiced periods list (the actual dropdown source-of-truth).
                const [summaryRes, periodsRes] = await Promise.all([
                    revenueAPI.getSummary(undefined, 'reporting_month'),
                    invoiceAPI.getAvailablePeriods().catch(() => ({ data: [] })),
                ]);
                setSummary(summaryRes.data);
                const periods = periodsRes.data || [];
                setAvailablePeriods(periods);

                // If URL gave us a month, keep it ONLY when it's actually available.
                // Otherwise pre-select the latest available period (newest month).
                setFormData(prev => {
                    const isPresetValid = presetMonth && periods.some(p => p.month === presetMonth);
                    if (isPresetValid) return { ...prev, month: presetMonth };
                    if (periods.length > 0) return { ...prev, month: periods[0].month };
                    return { ...prev, month: '' };
                });
            } catch (error) {
                console.error('Failed to fetch summary:', error);
            } finally {
                setSummaryLoading(false);
            }
        };

        fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleMonthChange = (month) => {
        setFormData(prev => ({ ...prev, month }));
    };

    // Amount is ALWAYS fixed to the recomputed payable for the selected reporting month
    // (sourced from /available-periods so we match the server's authoritative number).
    const lockedAmount = (() => {
        const period = availablePeriods.find(p => p.month === formData.month);
        if (period) return Number((period.amount || 0).toFixed(2));
        // Fallback for the (rare) case where the dropdown holds a value not in periods
        const monthData = summary?.by_month?.find(m => m.month === formData.month);
        return monthData ? Number((monthData.net_inr || 0).toFixed(2)) : 0;
    })();

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.month) {
            toast.error('Please select a reporting month');
            return;
        }

        if (lockedAmount <= 0) {
            toast.error('No net revenue available for the selected reporting month');
            return;
        }

        if (!attachment) {
            toast.error('Please upload your physical invoice document before submitting');
            return;
        }

        setLoading(true);

        try {
            // Create invoice (amount is fixed server-rule: always INR, always net_inr for the month)
            const invoiceResponse = await invoiceAPI.create({
                label_id: user.label_id,
                month: formData.month,
                amount: lockedAmount,
                currency: 'INR',
                notes: formData.notes || null
            });

            const invoiceId = invoiceResponse.data.id;

            // Upload physical invoice — required
            await invoiceAPI.uploadAttachment(invoiceId, attachment);

            toast.success('Invoice created successfully');
            navigate('/label/invoices');
        } catch (error) {
            console.error('Failed to create invoice:', error);
            toast.error(error.response?.data?.detail || 'Failed to create invoice');
        } finally {
            setLoading(false);
        }
    };

    // Dropdown source-of-truth: the un-invoiced periods endpoint.
    // Already excludes months that are pending/approved/paid for this label.
    const availableMonths = availablePeriods
        .filter(p => isValidMonth(p.month))
        .map(p => ({ month: p.month, net_inr: p.amount }));

    return (
        <div className="max-w-2xl mx-auto space-y-6" data-testid="create-invoice-page">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-heading font-bold tracking-tight">Create Invoice</h1>
                <p className="text-muted-foreground">Generate an invoice for your revenue earnings</p>
            </div>

            {/* Revenue Summary Card */}
            {!summaryLoading && summary && (
                <Card className="bg-secondary/10 border-secondary/20">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center">
                                <DollarSign className="w-6 h-6 text-secondary" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Net Revenue Available for Invoice</p>
                                <p className="text-2xl font-heading font-bold text-secondary">
                                    {formatCurrency(summary?.totals?.net_revenue_inr, 'INR')}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Invoice Form */}
            <Card className="bg-card/50 border-border/40">
                <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                        <FileText className="w-5 h-5 text-secondary" />
                        Invoice Details
                    </CardTitle>
                    <CardDescription>
                        Fill in the details for your invoice
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Month Selection */}
                        <div className="space-y-2">
                            <Label htmlFor="month">Reporting Month (Billing Period) *</Label>
                            <p className="text-xs text-muted-foreground -mt-1">
                                Invoices are raised against the reporting month (when platforms paid out), not the transaction month.
                            </p>
                            <Select 
                                value={formData.month} 
                                onValueChange={handleMonthChange}
                            >
                                <SelectTrigger className="bg-background/50" data-testid="month-select">
                                    <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
                                    <SelectValue placeholder="Select month" />
                                </SelectTrigger>
                                <SelectContent>
                                    {availableMonths.length === 0 ? (
                                        <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                                            No un-invoiced periods.<br />Every reporting month with revenue is already in an active invoice.
                                        </div>
                                    ) : (
                                        availableMonths.map((monthData) => (
                                            <SelectItem key={monthData.month} value={monthData.month}>
                                                <div className="flex justify-between items-center w-full gap-8">
                                                    <span>{formatMonth(monthData.month)}</span>
                                                    <span className="text-secondary font-mono text-sm">
                                                        {formatCurrency(monthData.net_inr, 'INR')}
                                                    </span>
                                                </div>
                                            </SelectItem>
                                        ))
                                    )}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Amount (locked to selected month's net INR) */}
                        <div className="space-y-2">
                            <Label htmlFor="amount">Invoice Amount (INR)</Label>
                            <div className="relative">
                                <Input
                                    id="amount"
                                    type="text"
                                    value={formatCurrencyFull(lockedAmount, 'INR')}
                                    readOnly
                                    disabled
                                    className="bg-muted/40 font-mono text-lg cursor-not-allowed"
                                    data-testid="amount-input"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground uppercase tracking-wider">
                                    Locked
                                </span>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                The invoice amount is fixed to your <span className="text-secondary font-medium">net INR revenue</span> for the selected reporting month. This cannot be edited.
                            </p>
                        </div>

                        {/* Notes */}
                        <div className="space-y-2">
                            <Label htmlFor="notes">Notes (Optional)</Label>
                            <Textarea
                                id="notes"
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                placeholder="Add any additional notes for this invoice..."
                                className="bg-background/50 min-h-[100px]"
                                data-testid="notes-input"
                            />
                        </div>

                        {/* File Upload — REQUIRED */}
                        <div className="space-y-2">
                            <Label>Attach Physical Invoice *</Label>
                            <div className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                                attachment ? 'border-secondary/60 bg-secondary/5' : 'border-border/50'
                            }`}>
                                <Upload className={`w-8 h-8 mx-auto mb-2 ${attachment ? 'text-secondary' : 'text-muted-foreground'}`} />
                                <p className="text-sm text-muted-foreground mb-3">
                                    Upload your signed invoice document (PDF, DOC, DOCX) — <span className="text-destructive">required</span>
                                </p>
                                <Input
                                    type="file"
                                    accept=".pdf,.doc,.docx"
                                    onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                                    className="max-w-xs mx-auto"
                                    data-testid="file-upload"
                                    required
                                />
                                {attachment && (
                                    <p className="mt-2 text-sm text-secondary">
                                        Selected: {attachment.name}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Submit */}
                        <div className="flex gap-4 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate('/label/invoices')}
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={loading || !attachment || lockedAmount <= 0}
                                className="flex-1 bg-secondary text-secondary-foreground hover:bg-secondary/90 disabled:opacity-50"
                                data-testid="submit-invoice-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <FileText className="w-4 h-4 mr-2" />
                                        Create Invoice
                                    </>
                                )}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};

export default CreateInvoicePage;
